import { aaveOraclesConfig, aaveOracles, aaveReserves } from '@1delta/data-sdk'
import { AaveOracleAbi } from '../../../abis/oracle/AaveOracle'
import {
  AaveQueryMeta,
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { formatUnits } from 'viem'
import { createMarketUid } from '../../../utils'

/**
 * Extended metadata for AAVE that includes BASE_CURRENCY_UNIT info
 */
export interface AaveQueryMetaExtended extends AaveQueryMeta {
  /** Index of BASE_CURRENCY_UNIT call in the calls array */
  baseCurrencyUnitIndex: number
  /** Number of asset price calls */
  assetCallCount: number
  /** Base asset override (if lender uses non-USD base) */
  baseAssetOverride?: string
  /** Fallback BASE_CURRENCY_UNIT if on-chain call reverts */
  baseCurrencyUnitOverride?: bigint
  /** Another AAVE fork to use as source for base asset USD price */
  baseAssetSource?: string
}

/**
 * Gets all available AAVE-style lenders for a chain
 */
function getAaveLendersForChain(chainId: string): string[] {
  const oracles = aaveOracles() ?? {}
  const reserves = aaveReserves() ?? {}
  const lenders: string[] = []

  for (const [lender, chains] of Object.entries(oracles)) {
    if (chains[chainId] && reserves[lender]?.[chainId]?.length > 0) {
      lenders.push(lender)
    }
  }

  return lenders
}

/**
 * Get multicall calls for AAVE oracles
 * Uses individual getAssetPrice calls per asset (with allowFailure=true)
 * Also fetches BASE_CURRENCY_UNIT for proper scaling
 */
export function getAaveCalls(
  chainId: string,
): FetcherResult<AaveQueryMetaExtended>[] {
  const lenders = getAaveLendersForChain(chainId)
  const oracles = aaveOracles() ?? {}
  const reserves = aaveReserves() ?? {}
  const results: FetcherResult<AaveQueryMetaExtended>[] = []

  for (const lender of lenders) {
    const oracle = oracles[lender]?.[chainId]
    const assets = reserves[lender]?.[chainId] ?? []

    if (!oracle || assets.length === 0) continue

    const calls: MulticallCall[] = []

    // First call: BASE_CURRENCY_UNIT to get scaling factor
    calls.push({
      address: oracle,
      name: 'BASE_CURRENCY_UNIT',
      params: [],
    })

    // Then individual getAssetPrice calls for each asset
    for (const asset of assets) {
      calls.push({
        address: oracle,
        name: 'getAssetPrice',
        params: [asset],
      })
    }

    // Check for lender overrides (base asset, baseCurrencyUnit fallback)
    const config = aaveOraclesConfig() ?? {}
    const lenderOverride = config[lender]?.[chainId]

    results.push({
      calls,
      meta: {
        oracle,
        assets,
        fork: lender,
        baseCurrencyUnitIndex: 0,
        assetCallCount: assets.length,
        baseAssetOverride: lenderOverride?.baseAsset,
        baseCurrencyUnitOverride: lenderOverride?.baseCurrencyUnit
          ? BigInt(lenderOverride.baseCurrencyUnit)
          : undefined,
        baseAssetSource: lenderOverride?.baseAssetSource,
      },
      lender,
    })
  }

  return results
}

/**
 * Parse AAVE oracle results into structured price entries
 * Uses BASE_CURRENCY_UNIT to properly scale prices
 *
 * For lenders with non-USD base assets (like KLAYBANK on KAIA),
 * prices need to be scaled by the base asset's USD price.
 */
export function parseAaveResults(
  data: any[],
  meta: AaveQueryMetaExtended,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []
  if (data.length === 0) return entries

  // First result is BASE_CURRENCY_UNIT
  // When an override is configured, always use it (takes precedence over on-chain value)
  let baseCurrencyUnitValue =
    meta.baseCurrencyUnitOverride ?? data[meta.baseCurrencyUnitIndex]

  if (!baseCurrencyUnitValue || baseCurrencyUnitValue === '0x') {
    return entries
  }

  // Calculate decimals from BASE_CURRENCY_UNIT (e.g., 10^8 -> 8 decimals)
  let decimals: number
  try {
    const baseCurrencyUnitBigInt = BigInt(baseCurrencyUnitValue.toString())
    decimals = Math.round(Math.log10(Number(baseCurrencyUnitBigInt)))
  } catch {
    return entries
  }

  // Get base asset USD price if this lender uses a non-USD base
  let baseAssetUSD = 1 // Default: USD-denominated
  if (meta.baseAssetOverride) {
    const baseAssetLc = meta.baseAssetOverride.toLowerCase()
    const baseOracleKey =
      tokenList?.[baseAssetLc]?.assetGroup ?? `${chainId}-${baseAssetLc}`
    baseAssetUSD = usdPrices[baseOracleKey] ?? usdPrices[baseAssetLc] ?? 1
  }

  // Parse each asset price
  for (let i = 0; i < meta.assetCallCount; i++) {
    const priceIndex = meta.baseCurrencyUnitIndex + 1 + i
    const rawPrice = data[priceIndex]
    const asset = meta.assets[i]

    if (rawPrice === undefined || rawPrice === '0x' || !asset) continue

    try {
      const rawPriceNum = Number(
        formatUnits(BigInt(rawPrice.toString()), decimals),
      )
      if (isNaN(rawPriceNum) || rawPriceNum === 0) continue

      // Scale by base asset USD price if not USD-denominated
      const priceUSD = rawPriceNum * baseAssetUSD

      // Only flag staticBase when this asset IS the base asset itself
      const isBaseAsset =
        meta.baseAssetOverride &&
        asset.toLowerCase() === meta.baseAssetOverride.toLowerCase()

      entries.push({
        asset: asset.toLowerCase(),
        price: rawPriceNum, // Raw price (in base asset terms)
        priceUSD,
        marketUid: createMarketUid(chainId, meta.fork, asset),
        staticBase: isBaseAsset || undefined,
        baseAsset: meta.baseAssetOverride,
      })
    } catch {
      // Skip invalid prices
      continue
    }
  }

  return entries
}

/**
 * Get ABI for AAVE oracle calls
 */
export function getAaveAbi() {
  return AaveOracleAbi
}

/**
 * AAVE Oracle Fetcher module
 */
export const aaveFetcher = {
  getCalls: getAaveCalls,
  parse: parseAaveResults,
  getAbi: getAaveAbi,
}
